import { colors, isDark, lighten, darken } from '@hub/design-system-base'

type AccessibleColor = {
  bg?: string
  color?: string
  hoverBg?: string
  hoverColor?: string
  activeBg?: string
  activeColor?: string
}

const HOVER_CHANGE = 10
const ACTIVE_CHANGE = 50

const accessibleColorScheme: { [key: string]: AccessibleColor } = Object.keys(
  colors
).reduce(
  (previousColors, color) => {
    const baseColor = isDark(color)({ colors }) ? 'white' : 'licorice'
    const hoverBg = isDark(color)({ colors })
      ? lighten(color, HOVER_CHANGE)({ colors })
      : darken(color, HOVER_CHANGE)({ colors })
    const hoverColor = isDark(hoverBg)({ colors }) ? 'white' : 'licorice'
    const activeBg = isDark(color)({ colors })
      ? lighten(color, ACTIVE_CHANGE)({ colors })
      : darken(color, ACTIVE_CHANGE)({ colors })
    const activeColor = isDark(activeBg)({ colors }) ? 'white' : 'licorice'
    return {
      [color]: {
        bg: color,
        color: baseColor,
        hoverBg,
        hoverColor,
        activeBg,
        activeColor,
      },
      // goes after on purpose to accept overrides from initial object
      ...previousColors,
    }
  },
  {
    white: {
      bg: 'white',
      color: 'licorice',
      hoverBg: 'smoke',
      hoverColor: 'licorice',
      activeBg: 'ghost',
      activeColor: 'licorice',
    },
    smoke: {
      bg: 'smoke',
      color: 'licorice',
      hoverBg: 'ghost',
      hoverColor: 'licorice',
      activeBg: 'manatee',
      activeColor: 'licorice',
    },
    ghost: {
      bg: 'ghost',
      color: 'licorice',
      hoverBg: 'manatee',
      hoverColor: 'licorice',
      activeBg: 'quartz',
      activeColor: 'white',
    },
    manatee: {
      bg: 'manatee',
      color: 'licorice',
      hoverBg: 'ghost',
      hoverColor: 'licorice',
      activeBg: 'smoke',
      activeColor: 'licorice',
    },
    quartz: {
      bg: 'quartz',
      color: 'white',
      hoverBg: 'manatee',
      hoverColor: 'white',
      activeBg: 'licorice',
      activeColor: 'white',
    },
    licorice: {
      bg: 'licorice',
      color: 'white',
      hoverBg: 'quartz',
      hoverColor: 'white',
      activeBg: 'manatee',
      activeColor: 'white',
    },
  } as { [key: string]: AccessibleColor }
)

export const buttonTheme = {
  baseStyle: {
    fontWeight: 'bold',
    fontSize: 'font-body',
    padding: 'spacing-md',
    fontFamily: 'body',
    textTransform: 'uppercase',
    backgroundColor: 'white',
  },
  defaultProps: {
    size: 'md',
    variant: 'solid',
    colorScheme: 'ghost',
  },
  sizes: {
    xs: {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-xs',
    },
    sm: {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-sm',
    },
    md: {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-md',
    },
    lg: {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-lg',
    },
    xl: {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-xl',
    },
    '2xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-2xl',
      padding: 'spacing-lg',
    },
    '3xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-3xl',
      padding: 'spacing-lg',
    },
    '4xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-4xl',
      padding: 'spacing-lg',
    },
    '5xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-5xl',
      padding: 'spacing-xl',
    },
    '6xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-6xl',
      padding: 'spacing-2xl',
    },
    '7xl': {
      height: '2.5em',
      minWidth: 'size-sm',
      fontSize: 'font-7xl',
      padding: 'spacing-2xl',
    },
  },
  variants: {
    solid: function variantSolid({ colorScheme }: { colorScheme: string }) {
      const accessibleColors = accessibleColorScheme[colorScheme]
      return {
        backgroundColor: accessibleColors.bg,
        color: accessibleColors.color,
        _hover: {
          backgroundColor: accessibleColors.hoverBg,
          color: accessibleColors.hoverColor,
        },
        _active: {
          backgroundColor: accessibleColors.activeBg,
          color: accessibleColors.activeColor,
        },
      }
    },
    outline: function variantOutline({ colorScheme }: { colorScheme: string }) {
      const accessibleColors = accessibleColorScheme[colorScheme]
      return {
        backgroundColor: 'transparent',
        color: accessibleColors.bg,
        border: '2px solid',
        borderColor: accessibleColors.bg,
        _hover: {
          color: accessibleColorScheme.hoverBg,
          borderColor: accessibleColors.hoverBg,
        },
      }
    },
    transparent: function variantTransparent({
      colorScheme,
    }: {
      colorScheme: string
    }) {
      const accessibleColors = accessibleColorScheme[colorScheme]
      return {
        backgroundColor: 'transparent',
        color: accessibleColors.bg,
        _hover: {
          color: accessibleColors.hoverBg,
        },
        minWidth: '44px', // keeps the hit target big enough
      }
    },
    link: function variantLink({ colorScheme }: { colorScheme: string }) {
      const accessibleColors = accessibleColorScheme[colorScheme]
      return {
        backgroundColor: 'transparent',
        color: accessibleColors.bg,
        _hover: {
          textDecoration: 'underline',
        },
        minWidth: '44px', // keeps the hit target big enough
      }
    },
  },
}
